// JS mirror of backend services/assembly_calc.py.
// Used by AssemblyForm + AssemblyLiveCalcPanel for live UI recalculation.
import { computeNpd, computeYoySchedule } from "@/utils/npdCalc";

const DEFAULT_MATERIAL_THRESHOLD_KG = 2000.0;

// Mirror of backend compute_treatment_cost (services/npd_calc.py) — multi-mode.
// Assemblies have no assembly-level weight, so per_kg resolves to 0 (backend passes 0).
function _computeTreatment(t) {
  if (!t) return 0;
  if (typeof t === "number") return Number(t) || 0;
  const mode = t.mode || "per_piece";
  const rate = Number(t.rate) || 0;
  if (mode === "per_piece") return rate;
  if (mode === "per_kg") return rate * 0;
  if (mode === "per_sqin") return rate * (Number(t.surface_area_sqin) || 0);
  return 0;
}

export function getAssemblyToolMaintenancePct(a) {
  let raw = a.assembly_tool_maintenance_pct;
  if (raw === undefined || raw === null) return 0;
  raw = Number(raw);
  if (raw > 0 && raw < 1.5) return raw * 100;
  return raw;
}

// R34 — mirror of services/assembly_calc.py::_compute_sourcing_rollup
function _computeSourcingRollup(asm, componentNpds) {
  const npdById = {};
  (componentNpds || []).forEach((n) => { npdById[n.npd_id] = n; });

  const volumes = asm.volumes || [];
  let peak = null;
  for (const v of volumes) {
    const a = Number(v.annual_volume_pcs) || 0;
    if (!peak || a > (Number(peak.annual_volume_pcs) || 0)) peak = v;
  }
  const peakMonthlyPcs = Number(peak?.monthly_volume_pcs) || 0;
  const peakYearLabel = peak?.year_label || (peak ? `Year ${peak.year_number}` : null);
  const sourcingUnavailable = peakMonthlyPcs <= 0;

  const groups = new Map();  // key: `${material_id}||${material_grade}`
  const moqCandidates = [];
  let moqUnresolved = 0;

  for (const ac of (asm.components || [])) {
    const qty = Number(ac.qty_per_assembly) || 0;
    const npd = npdById[ac.npd_id];
    if (!npd || qty <= 0 || npd._is_assembly) continue; // nested assemblies excluded from material rollup

    const gross = Number(npd.gross_weight) || 0;
    const matId = npd.material_id || "__unknown__";
    const matGrade = npd.material_grade || "";
    const key = `${matId}||${matGrade}`;

    if (!groups.has(key)) {
      const threshold =
        Number(npd.material_threshold_kg) ||
        Number(npd.monthly_rm_threshold_kg) ||
        DEFAULT_MATERIAL_THRESHOLD_KG;
      const label = npd.material_label || matGrade
        || (matId !== "__unknown__" ? matId.slice(0, 8) : "(material not set)");
      groups.set(key, {
        material_id: matId,
        material_grade: matGrade,
        material_label: label,
        threshold_kg: Math.round(threshold * 1000) / 1000,
        contributing_components: [],
        thicknesses: [],
        _total: 0,
      });
    }
    const contributionKg = gross * qty * peakMonthlyPcs;
    const g = groups.get(key);
    const thk = Math.round((Number(npd.thickness_mm) || 0) * 100) / 100;
    if (thk > 0 && !g.thicknesses.includes(thk)) g.thicknesses.push(thk);
    g.contributing_components.push({
      npd_id: npd.npd_id,
      npd_number: npd.npd_number,
      part_name: npd.part_name,
      qty_per_assembly: qty,
      gross_weight_kg: Math.round(gross * 10000) / 10000,
      contribution_kg: Math.round(contributionKg * 1000) / 1000,
    });
    g._total += contributionKg;

    const moqKg = Number(npd.moq_kg) || 0;
    if (moqKg > 0 && gross > 0) {
      const childMoqBatchPcs = moqKg / gross;
      const assembliesRequired = Math.ceil(childMoqBatchPcs / qty);
      moqCandidates.push({
        npd_id: npd.npd_id,
        npd_number: npd.npd_number,
        part_name: npd.part_name,
        moq_kg: Math.round(moqKg * 1000) / 1000,
        gross_weight_kg: Math.round(gross * 10000) / 10000,
        qty_per_assembly: qty,
        child_moq_batch_pcs: Math.round(childMoqBatchPcs),
        assemblies_required: assembliesRequired,
      });
    } else {
      moqUnresolved += 1;
    }
  }

  const monthlyRmByMaterial = [];
  let assemblyLowVolumeFlag = false;
  if (!sourcingUnavailable) {
    for (const g of groups.values()) {
      const total = g._total;
      delete g._total;
      g.monthly_rm_kg = Math.round(total * 1000) / 1000;
      const thk = (g.thicknesses || []).slice().sort((a, b) => a - b);
      delete g.thicknesses;
      g.thicknesses_mm = thk;
      g.thickness_label = thk.map((t) => `${t}`).join(", ");
      g.low_volume_flag = total > 0 && total < g.threshold_kg;
      if (g.low_volume_flag) assemblyLowVolumeFlag = true;
      monthlyRmByMaterial.push(g);
    }
  }

  let customerMoqPcs = null;
  let customerMoqDriver = null;
  if (moqCandidates.length > 0) {
    const binding = moqCandidates.reduce(
      (b, c) => (c.assemblies_required > b.assemblies_required ? c : b),
      moqCandidates[0],
    );
    customerMoqPcs = binding.assemblies_required;
    customerMoqDriver = {
      npd_id: binding.npd_id,
      npd_number: binding.npd_number,
      part_name: binding.part_name,
      assemblies_required: binding.assemblies_required,
      reason: `${binding.moq_kg} kg / ${binding.gross_weight_kg} kg/pc = ${binding.child_moq_batch_pcs.toFixed(0)} pcs; ÷ qty ${binding.qty_per_assembly} = ${binding.assemblies_required} assemblies`,
    };
  }

  return {
    sourcing_rollup_unavailable: sourcingUnavailable,
    assembly_peak_monthly_pcs: peakMonthlyPcs,
    assembly_peak_year_label: peakYearLabel,
    monthly_rm_by_material: monthlyRmByMaterial,
    assembly_low_volume_flag: assemblyLowVolumeFlag,
    customer_moq_pcs: customerMoqPcs,
    customer_moq_driver: customerMoqDriver,
    customer_moq_components: moqCandidates,
    customer_moq_unresolved_count: moqUnresolved,
    customer_moq_is_lower_bound: customerMoqPcs !== null && moqUnresolved > 0,
  };
}

export function computeAssembly(asm, componentNpds) {
  const byId = {};
  (componentNpds || []).forEach((n) => { byId[n.npd_id] = n; });

  const componentLines = [];
  let componentsTotal = 0;
  for (const ac of asm.components || []) {
    const npd = byId[ac.npd_id];
    const qty = Number(ac.qty_per_assembly) || 0;
    if (!npd) {
      componentLines.push({
        npd_id: ac.npd_id, qty_per_assembly: qty, missing: true,
        unit_price: 0, line_total: 0,
      });
      continue;
    }
    // Nested assembly sub-part contributes its own grand total; parts compute live.
    const unitPrice = npd._is_assembly
      ? (Number(npd.grand_total) || 0)
      : (Number(computeNpd(npd).grand_total) || 0);
    const lineTotal = qty * unitPrice;
    componentsTotal += lineTotal;
    componentLines.push({
      npd_id: ac.npd_id,
      npd_number: npd.npd_number,
      part_name: npd.part_name,
      part_number: npd.part_number,
      qty_per_assembly: qty,
      unit_price: Math.round(unitPrice * 10000) / 10000,
      line_total: Math.round(lineTotal * 10000) / 10000,
      notes: ac.notes || "",
      missing: false,
    });
  }

  const assyProcessCost = (asm.assembly_processes || []).reduce((s, p) => s + (Number(p.cost) || 0), 0);
  const assyBopCost = (asm.assembly_bop_items || []).reduce(
    (s, b) => s + (Number(b.qty) || 0) * (Number(b.unit_price) || 0), 0,
  );
  const surf = _computeTreatment(asm.assembly_surface_treatment);
  const heat = _computeTreatment(asm.assembly_heat_treatment);
  // R48 — Assembly Tool Maintenance: % of process cost OR fixed lumpsum
  let assyToolMaint;
  if (asm.assembly_tool_maintenance_overridden) {
    assyToolMaint = Number(asm.assembly_tool_maintenance_fixed) || 0;
  } else {
    const tmPct = getAssemblyToolMaintenancePct(asm);
    assyToolMaint = assyProcessCost * tmPct / 100;
  }
  const assyTreatmentTotal = surf + heat + assyToolMaint;

  const assySubTotal = componentsTotal + assyProcessCost + assyBopCost + assyTreatmentTotal;

  const ctx = {
    components_total: componentsTotal,
    process_cost: assyProcessCost,
    sub_total: assySubTotal,
    gross_rmc: componentsTotal,
    net_rmc: componentsTotal,
  };
  const overheadsCalculated = (asm.assembly_overheads || []).map((row) => {
    const v = Number(row.value) || 0;
    const calc = row.basis === "lump_sum" ? v : v * (ctx[row.basis] || 0);
    return { ...row, calculated: Math.round(calc * 10000) / 10000 };
  });
  const overheadsAndMargin = overheadsCalculated.reduce((s, r) => s + r.calculated, 0);
  const grandTotalBeforeAdj = assySubTotal + overheadsAndMargin;
  let grandTotal = grandTotalBeforeAdj;
  let sellingPrice = grandTotal;

  // R46 — Assembly Section F: Custom Adjustments
  const customAdjIn = Array.isArray(asm.assembly_custom_adjustments) ? asm.assembly_custom_adjustments : [];
  const customAdjCalculated = [];
  let customAdjTotal = 0;
  for (const row of customAdjIn) {
    const mode = (row.mode || "pct").toLowerCase();
    const value = Number(row.value) || 0;
    const amount = mode === "pct" ? grandTotalBeforeAdj * (value / 100) : value;
    customAdjCalculated.push({
      label: row.label || "",
      mode, value,
      amount: Math.round(amount * 10000) / 10000,
    });
    customAdjTotal += amount;
  }
  if (customAdjIn.length > 0) {
    grandTotal = grandTotalBeforeAdj + customAdjTotal;
    sellingPrice = grandTotal;
  }
  const marginPct = grandTotalBeforeAdj > 0 ? (overheadsAndMargin / grandTotalBeforeAdj) * 100 : 0;

  const yoy_schedule = computeYoySchedule(
    grandTotal,
    asm.yoy_reduction_pct,
    asm.project_life_years,
    asm.volumes,
    asm.yoy_apply_years,
  );
  const total_project_value = Math.round(yoy_schedule.reduce((s, r) => s + r.annual_value, 0) * 100) / 100;

  // Development (one-time tooling) cost — mirrors backend assembly_calc.py.
  // Count each distinct sub-part's tooling_cost once; skip amortized parts
  // (their tool cost is already inside their per-pc price); add the typed amount.
  const _devSeen = new Set();
  let componentsDevelopmentCost = 0;
  for (const ac of asm.components || []) {
    const nid = ac.npd_id;
    if (!nid || _devSeen.has(nid)) continue;
    _devSeen.add(nid);
    const npd = byId[nid];
    if (!npd) continue;
    if (npd._is_assembly) {
      // Nested assembly rolls up its own total development cost.
      componentsDevelopmentCost += Number(npd.development_cost_total) || 0;
      continue;
    }
    if (npd.amortize_tooling) continue;
    componentsDevelopmentCost += Number(npd.tooling_cost) || 0;
  }
  const assemblyDevelopmentCost = Number(asm.assembly_development_cost) || 0;
  const developmentCostTotal = componentsDevelopmentCost + assemblyDevelopmentCost;

  const sourcing = _computeSourcingRollup(asm, componentNpds);

  return {
    component_lines: componentLines,
    components_total: componentsTotal,
    assembly_process_cost: assyProcessCost,
    assembly_bop_cost: assyBopCost,
    assembly_surface_cost: surf,
    assembly_heat_cost: heat,
    assembly_tool_maintenance: assyToolMaint,
    assembly_treatment_total: assyTreatmentTotal,
    sub_total: assySubTotal,
    overheads_calculated: overheadsCalculated,
    overheads_and_margin: overheadsAndMargin,
    // R46 — Section F outputs
    grand_total_before_adj: Math.round(grandTotalBeforeAdj * 10000) / 10000,
    assembly_custom_adjustments_calculated: customAdjCalculated,
    assembly_custom_adjustments_total: Math.round(customAdjTotal * 10000) / 10000,
    components_development_cost: Math.round(componentsDevelopmentCost * 100) / 100,
    assembly_development_cost: Math.round(assemblyDevelopmentCost * 100) / 100,
    development_cost_total: Math.round(developmentCostTotal * 100) / 100,
    grand_total: grandTotal,
    selling_price: sellingPrice,
    margin_amount: overheadsAndMargin,
    margin_pct: marginPct,
    yoy_schedule, total_project_value,
    yoy_reduction_pct: Number(asm.yoy_reduction_pct) || 0,
    project_life_years: Number(asm.project_life_years) || 1,
    yoy_apply_years: asm.yoy_apply_years ?? null,
    ...sourcing,
  };
}
